// src/components/agent/ProtectedView.js
// Best-effort screenshot / copy deterrent for sensitive content
// (contract templates and signed contracts). Browsers cannot truly
// stop OS-level screenshots — but we can:
//   - Disable text selection (CSS user-select: none)
//   - Disable right-click → Save image / Copy
//   - Disable drag (so images can't be dragged out)
//   - Overlay a faint diagonal "HomeIQa · Confidential" watermark
//     showing the viewer's name + email + the timestamp so any photo
//     of the screen is traceable
//   - Disable keyboard print shortcut and replace browser print with
//     a clear refusal toast
//
// This is a deterrent, not a guarantee. Determined adversaries will
// always succeed; the goal is to discourage casual copying and make
// any leak forensically traceable.
import React, { useEffect, useRef, useState } from 'react';

export default function ProtectedView({ viewer, label = 'Confidential', children, hideOnHide = true, blurOutsideTab = true }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(true);

  // Block right-click, drag, and copy keyboard shortcuts inside this view.
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return;
    const block = (e) => { e.preventDefault(); return false; };
    const blockKey = (e) => {
      // Allow nothing destructive: copy/cut/save/print
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && ['c', 'x', 'p', 's'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    node.addEventListener('contextmenu', block);
    node.addEventListener('dragstart', block);
    node.addEventListener('selectstart', block);
    node.addEventListener('copy', block);
    node.addEventListener('cut', block);
    document.addEventListener('keydown', blockKey);
    return () => {
      node.removeEventListener('contextmenu', block);
      node.removeEventListener('dragstart', block);
      node.removeEventListener('selectstart', block);
      node.removeEventListener('copy', block);
      node.removeEventListener('cut', block);
      document.removeEventListener('keydown', blockKey);
    };
  }, []);

  // Hide the content if the user switches tabs / minimizes — protects against
  // some screen-recording software that captures the focused window only.
  useEffect(() => {
    if (!hideOnHide && !blurOutsideTab) return;
    const onVis = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [hideOnHide, blurOutsideTab]);

  const watermarkText = (() => {
    const parts = [
      label.toUpperCase(),
      viewer?.name || viewer?.email || 'Authorized viewer',
      new Date().toLocaleString()
    ].filter(Boolean);
    return parts.join('  ·  ');
  })();

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
    >
      {visible || !blurOutsideTab ? children : (
        <div style={hiddenStyle}>
          <div style={hiddenInner}>
            <div style={hiddenIcon}>🔒</div>
            <div style={hiddenTitle}>Hidden while tab is in the background</div>
            <div style={hiddenSub}>Switch back to the HomeIQa tab to view this contract.</div>
          </div>
          {/* Render the children invisibly to preserve layout */}
          <div style={{ visibility: 'hidden' }}>{children}</div>
        </div>
      )}

      {/* Diagonal watermark — repeating so even small screenshots include it. */}
      <div style={watermarkOverlay} aria-hidden="true">
        {Array.from({ length: 28 }).map((_, i) => (
          <div key={i} style={watermarkLine}>{watermarkText}</div>
        ))}
      </div>
    </div>
  );
}

const watermarkOverlay = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 50,
  overflow: 'hidden',
  transform: 'rotate(-22deg)',
  transformOrigin: 'center center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  whiteSpace: 'nowrap',
  fontFamily: "'DM Sans', sans-serif",
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  fontSize: 12,
  fontWeight: 600,
  color: 'rgba(107,30,46,0.10)'
};
const watermarkLine = { padding: '4px 0', width: '160%', marginLeft: '-30%' };

const hiddenStyle = {
  position: 'relative',
  background: '#1C1C1C',
  borderRadius: 12,
  minHeight: 280,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const hiddenInner = { textAlign: 'center', color: '#fff', position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' };
const hiddenIcon = { fontSize: 36, marginBottom: 8 };
const hiddenTitle = { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, marginBottom: 6 };
const hiddenSub = { fontSize: 13, color: 'rgba(255,255,255,0.7)', maxWidth: 360, lineHeight: 1.5 };
